"use client";

import { useCallback, useId, useRef, useState } from "react";
import { EMAIL_FORMAT, emailSuggestionMessage } from "@shared/formats/email";
import { Button } from "@/components/ui/button";
import { useFieldMessage } from "@/components/ui/field-control";

type InputRef = React.Ref<HTMLInputElement> | undefined;

function assignRef(ref: InputRef, node: HTMLInputElement | null): void {
  if (typeof ref === "function") {
    ref(node);
    return;
  }
  if (ref) ref.current = node;
}

function setNativeValue(node: HTMLInputElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  if (setter) setter.call(node, value);
  else node.value = value;
  node.dispatchEvent(new Event("input", { bubbles: true }));
}

function withSuggestion(
  describedBy: string | undefined,
  suggestionId: string | undefined,
): string | undefined {
  return suggestionId ? [describedBy, suggestionId].filter(Boolean).join(" ") : describedBy;
}

export function useEmailSuggestion(options: {
  enabled: boolean;
  value: React.InputHTMLAttributes<HTMLInputElement>["value"];
  defaultValue: React.InputHTMLAttributes<HTMLInputElement>["defaultValue"];
  describedBy: string | undefined;
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
  ref: InputRef;
}) {
  const { enabled, value, defaultValue, describedBy, onChange, ref } = options;
  const node = useRef<HTMLInputElement | null>(null);
  const [typed, setTyped] = useState(() =>
    defaultValue === undefined ? "" : String(defaultValue),
  );
  const suggestionId = useId();
  const current = value === undefined || value === null ? typed : String(value);
  const suggestion = enabled ? EMAIL_FORMAT.suggest(current) : undefined;

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setTyped(event.currentTarget.value);
      onChange?.(event);
    },
    [onChange],
  );

  const setRef = useCallback(
    (element: HTMLInputElement | null) => {
      node.current = element;
      assignRef(ref, element);
    },
    [ref],
  );

  const apply = useCallback(() => {
    const element = node.current;
    if (!element || !suggestion) return;
    setNativeValue(element, suggestion);
    element.focus();
  }, [suggestion]);

  const inputProps = enabled
    ? { onChange: handleChange, ref: setRef, "aria-describedby": withSuggestion(describedBy, suggestion && suggestionId) }
    : { onChange, ref, "aria-describedby": describedBy };

  return { suggestion, suggestionId, inputProps, apply };
}

export function EmailSuggestion({
  id,
  suggestion,
  onApply,
}: {
  id: string;
  suggestion: string;
  onApply: () => void;
}) {
  useFieldMessage(id, "suggestion", true);
  return (
    <p id={id} role="status" data-slot="email-suggestion" className="text-xs">
      <Button type="button" variant="link" size="bare" onClick={onApply}>
        {emailSuggestionMessage(suggestion)}
      </Button>
    </p>
  );
}
